import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  AutoAwesome as AiIcon,
  CheckCircle as CheckIcon,
  Cancel as WrongIcon,
  Lightbulb as HintIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api, { getMockExamApiPrefix, getMockExamPortalHomePath } from '../../../api';
import QuestionPaperLayout from '../../QuestionBank/components/QuestionPaperLayout';
import HtmlWithLatex from '../../QuestionBank/components/HtmlWithLatex';
import FormatInstructions from '../../../components/FormatInstructions';
import VideoExplanationPlayer from '../../../components/VideoExplanationPlayer';
import MarksBreakdownTable from '../components/MarksBreakdownTable';
import InteractiveDiagramCanvas from '../components/InteractiveDiagramCanvas';
import { interactiveRevealValue } from '../utils/interactiveRevealValue';
import {
  isFillQuestionType,
  coerceFillAnswersArray,
  isPerBlankFillDisplay,
} from '../../../utils/fillQuestionUtils';
import {
  singleValueMatches,
  splitMultiValueAnswer,
  extractCorrectAlternatives,
  countQuestionNumericAnswerEquivalent,
} from '../../../utils/answerMatchUtils';

// Strip embedded sub-question labels (a) … b) …) from a grouped passage/stem.
// When the AI stores the full question in the parent, the passage should only show the context.
// Renders the student's plotted shape and the correct shape for an interactive drawing answer.
const InteractiveAnswerBlock = ({ row }) => {
  const correct = row?.correct_interactive || null;
  const gridSpec = row?.answer_data?.interactive || correct;
  const your = Array.isArray(row?.your_answer_interactive) ? row.your_answer_interactive : null;
  if (!correct && !gridSpec) return null;
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', my: 1 }}>
      <Box sx={{ flex: 1, minWidth: 220 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Your shape</Typography>
        {your && your.length > 0 && gridSpec ? (
          <InteractiveDiagramCanvas spec={gridSpec} imageUrl={gridSpec.image_url || row.image_url || ''} readOnly revealValue={your} />
        ) : (
          <Typography variant="body2" color="text.disabled">—</Typography>
        )}
      </Box>
      {correct && (
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Correct shape</Typography>
          <InteractiveDiagramCanvas spec={correct} imageUrl={correct.image_url || row.image_url || ''} readOnly revealValue={interactiveRevealValue(correct)} />
        </Box>
      )}
    </Box>
  );
};

const cleanStemText = (text) => {
  if (!text) return text;
  const mA = text.match(/(?:\s|^)(a\s*[).]\s+\S)/i);
  const mB = text.match(/\s+b\s*[).]\s+\S/i);
  if (mA && mB && mA.index > 30) {
    return text.substring(0, mA.index).trimEnd();
  }
  return text;
};

// Extract the core question text from a sub-question that includes passage preamble.
// Finds the last occurrence of the sub-label (e.g. "a)" / "b)") at a line boundary
// and returns everything after it, discarding the repeated context.
const extractSubCore = (text, label) => {
  if (!text || !label) return text;
  const esc = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`(?:^|[\\n\\r])[ \\t]*${esc}[ \\t]*[).][ \\t]*`, 'gi');
  let lastMatch = null;
  let m;
  while ((m = pattern.exec(text)) !== null) {
    lastMatch = m;
  }
  if (lastMatch && lastMatch.index > 5) {
    return text.substring(lastMatch.index + lastMatch[0].length).trim();
  }
  return text;
};

const formatAnswer = (value) => {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') {
    // TypeDraw answer: { text, drawing } — show only the typed text
    if ('text' in value || 'drawing' in value) {
      const text = value.text;
      if (text) return String(text);
      if (value.drawing) return '[Drawing only]';
      return '—';
    }
    // A matching answer is a { leftItem: rightItem } map — read it back as the links the student drew,
    // in the same "a → b" shape the correct answer is shown in.
    const entries = Object.entries(value).filter(([, v]) => String(v ?? '').trim() !== '');
    if (entries.length > 0 && entries.every(([, v]) => typeof v === 'string')) {
      return entries.map(([k, v]) => `${k} → ${v}`).join('; ');
    }
    return entries
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
      .join(' | ');
  }
  return String(value);
};

// All answer matching lives in the shared answerMatchUtils.js (imported above) so fixes there
// (time leniency, unit conversion, comma-decimal guards, word-boundary matching) apply here too
// instead of drifting out of sync. The flexible variants add the multi-fill array + TypeDraw
// handling this page needs; a subset of the required values displays as partial, never correct.
// Extract the plain-text portion from a possibly TypeDraw { text, drawing } value
const extractAnswerText = (value) => {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    if ('text' in value || 'drawing' in value) return value.text ?? '';
  }
  return value;
};

// Time format, unit conversion, LaTeX fraction, ordinal, and word-boundary matching all live in the
// shared answerMatchUtils.js (imported above) so fixes there (e.g. dot-vs-colon time leniency,
// "Super market"/"Supermarket" word-boundary matching) apply here too instead of drifting out of sync.

/**
 * Returns true when submitted is a single value that matches any one (but not all) of the
 * comma-separated correct values. Used for partial display colouring.
 * e.g. submitted "31", correct "31, 62" → partially correct
 */
const isAnswerPartial = (submitted, correct, questionText = '') => {
  const submittedVal = extractAnswerText(submitted);
  if (submittedVal == null || submittedVal === '' || submittedVal === '—') return false;
  if (correct == null || correct === '' || correct === '—') return false;
  if (Array.isArray(submittedVal) || Array.isArray(correct)) return false;
  const correctStr = String(correct);
  if (countQuestionNumericAnswerEquivalent(submittedVal, correctStr, questionText)) return false;
  // Never treat alternatives-format answers as partial: "1,60 (or) 2,30" is not a list of required values
  if (extractCorrectAlternatives(correctStr).length > 1) return false;
  const correctParts = splitMultiValueAnswer(correctStr);
  if (correctParts.length <= 1) return false;
  const submittedParts = splitMultiValueAnswer(String(submittedVal));
  // Only partial if student gave fewer values than required and at least one matches
  if (submittedParts.length >= correctParts.length) return false;
  return submittedParts.some((sp) => correctParts.some((cp) => singleValueMatches(sp, cp)));
};

/**
 * Compare submitted answer vs correct answer for display purposes.
 * Handles multi-fill arrays, comma/newline-separated values, slash-separated alternatives,
 * unit conversions, and TypeDraw { text, drawing } objects.
 */
const isAnswerCorrect = (submitted, correct, questionText = '') => {
  const submittedVal = extractAnswerText(submitted);
  if (submittedVal == null || submittedVal === '' || submittedVal === '—') return false;
  if (correct == null || correct === '' || correct === '—') return false;
  submitted = submittedVal; // eslint-disable-line no-param-reassign

  if (countQuestionNumericAnswerEquivalent(submitted, correct, questionText)) return true;

  // Both arrays — multi-fill: each blank index must match
  if (Array.isArray(submitted) && Array.isArray(correct)) {
    if (correct.length === 0) return false;
    return correct.every((c, i) => singleValueMatches(submitted[i] ?? '', c));
  }

  // Submitted array, correct string — single blank
  if (Array.isArray(submitted) && !Array.isArray(correct)) {
    return singleValueMatches(submitted[0] ?? '', correct);
  }

  // Correct array, submitted string — single blank with alternatives
  if (!Array.isArray(submitted) && Array.isArray(correct)) {
    return singleValueMatches(submitted, correct[0] ?? '');
  }

  // Direct single-value comparison (includes unit conversion)
  if (singleValueMatches(submitted, correct)) return true;

  // Multi-value set matching: correct has comma-separated list ("95cm, 1.12m, 1.14m")
  // and student may have entered newline-separated or comma-separated values
  const correctParts = splitMultiValueAnswer(String(correct));
  const submittedParts = splitMultiValueAnswer(String(submitted));

  if (correctParts.length > 1 && submittedParts.length > 1) {
    let matched = 0;
    const usedSubmitted = new Set();
    for (const cPart of correctParts) {
      for (let si = 0; si < submittedParts.length; si++) {
        if (usedSubmitted.has(si)) continue;
        if (singleValueMatches(submittedParts[si], cPart)) {
          matched++;
          usedSubmitted.add(si);
          break;
        }
      }
    }
    // Consider correct if at least all submitted parts match correct parts
    // (partial match still shows as correct for display if student got most right)
    if (matched === correctParts.length) return true;
    if (matched > 0 && matched >= submittedParts.length) return true;
  }

  return false;
};

// ---- AI Feedback Panel for a single question ----
const AiFeedbackPanel = ({ questionId, aiFeedbackMap, aiFeedbackStatus, questionMarks, ruleIsCorrect, ruleIsPartial }) => {
  const aiq = aiFeedbackMap?.[String(questionId)];

  if (aiFeedbackStatus === 'pending' || aiFeedbackStatus === 'processing') {
    return (
      <Box
        sx={{
          mt: 1.5,
          p: 1.5,
          borderRadius: 2,
          bgcolor: '#f3f0ff',
          border: '1px solid #e0d7ff',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <CircularProgress size={14} sx={{ color: '#7c3aed' }} />
        <Typography variant="caption" sx={{ color: '#7c3aed', fontStyle: 'italic' }}>
          AI feedback is being generated…
        </Typography>
      </Box>
    );
  }

  if (aiFeedbackStatus === 'failed' || !aiq) {
    return null;
  }

  const aiIsCorrect = aiq.is_correct;
  const partialScore = aiq.partial_score;
  const isAiPartial = partialScore != null && partialScore > 0 && partialScore < 1;

  // Conflict: AI says incorrect/partial, but rule-based says fully correct.
  const aiConflictsWithRuleCorrect = ruleIsCorrect === true && aiIsCorrect !== true;

  // Conflict: AI says incorrect, but rule-based says partially correct.
  // e.g. student typed "31", correct is "31, 62" — AI marks wrong, rule says partial.
  // The rule-based scorer has precise multi-value logic; trust it over AI here.
  const aiConflictsWithRulePartial = ruleIsPartial === true && aiIsCorrect === false && !isAiPartial;

  const anyConflict = aiConflictsWithRuleCorrect || aiConflictsWithRulePartial;

  // Effective verdict: when there's a conflict, trust the rule-based result
  let effectiveCorrect = aiIsCorrect;
  let effectivePartial = isAiPartial;

  if (aiConflictsWithRuleCorrect) {
    effectiveCorrect = true;
    effectivePartial = false;
  } else if (aiConflictsWithRulePartial) {
    effectiveCorrect = false;
    effectivePartial = true;
  }

  const earnedMarks = partialScore != null
    ? Math.round((partialScore * (questionMarks ?? 1)) * 100) / 100
    : null;

  let bgcolor = '#f8f9fa';
  let borderColor = '#e0e0e0';
  if (effectiveCorrect === true) { bgcolor = '#f0fdf4'; borderColor = '#bbf7d0'; }
  else if (effectivePartial) { bgcolor = '#fffbeb'; borderColor = '#fde68a'; }
  else if (effectiveCorrect === false) { bgcolor = '#fff5f5'; borderColor = '#fecaca'; }

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: 2,
        bgcolor,
        border: '1px solid',
        borderColor,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, flexWrap: 'wrap' }}>
        <AiIcon sx={{ fontSize: 15, color: '#7c3aed' }} />
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          AI Feedback
        </Typography>
        {effectiveCorrect === true && !effectivePartial && (
          <Chip
            icon={<CheckIcon sx={{ fontSize: 13 }} />}
            label="Correct"
            size="small"
            sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600, height: 20, fontSize: '0.7rem' }}
          />
        )}
        {effectivePartial && (
          <Chip
            label={
              aiConflictsWithRulePartial
                ? 'Partially correct'
                : `Partial — ${earnedMarks != null ? earnedMarks : Math.round(partialScore * 100) + '%'} marks`
            }
            size="small"
            sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, height: 20, fontSize: '0.7rem' }}
          />
        )}
        {effectiveCorrect === false && !effectivePartial && (
          <Chip
            icon={<WrongIcon sx={{ fontSize: 13 }} />}
            label="Incorrect"
            size="small"
            sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 600, height: 20, fontSize: '0.7rem' }}
          />
        )}
        {aiConflictsWithRuleCorrect && (
          <Chip
            label="Re-evaluated: Correct"
            size="small"
            sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600, height: 20, fontSize: '0.7rem' }}
          />
        )}
        {aiConflictsWithRulePartial && (
          <Chip
            label="Re-evaluated: Partially correct"
            size="small"
            sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, height: 20, fontSize: '0.7rem' }}
          />
        )}
      </Box>
      {aiq.feedback && (
        <Box sx={{ fontSize: '0.875rem', color: 'text.primary', lineHeight: 1.6, mb: aiq.hint ? 0.75 : 0 }}>
          <HtmlWithLatex html={aiq.feedback} />
        </Box>
      )}
      {aiConflictsWithRuleCorrect && (
        <Typography variant="caption" sx={{ color: '#166534', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
          Note: Your answer contains the correct value — it has been accepted by the scoring system.
        </Typography>
      )}
      {aiConflictsWithRulePartial && (
        <Typography variant="caption" sx={{ color: '#92400e', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
          Note: Your answer matches one of the required values — it has been counted as partially correct.
        </Typography>
      )}
      {aiq.hint && !anyConflict && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5 }}>
          <HintIcon sx={{ fontSize: 15, color: '#d97706', mt: 0.2, flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: '#92400e', fontStyle: 'italic', lineHeight: 1.5 }}>
            <strong>Tip:</strong> {aiq.hint}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const StudentMockExamReview = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [reviewTab, setReviewTab] = useState('questions');

  // AI feedback state
  const [aiFeedbackStatus, setAiFeedbackStatus] = useState('pending');
  const [aiFeedbackMap, setAiFeedbackMap] = useState({});
  const [aiOverall, setAiOverall] = useState(null);
  const [aiScore, setAiScore] = useState(null);
  // aiPercentage is fetched but display is computed inline from aiScore/total_marks
  const [, setAiPercentage] = useState(null);
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);
  const MAX_POLLS = 20;

  useEffect(() => {
    const handleExamModeChanged = () => {
      window.location.reload();
    };
    window.addEventListener("exam-mode-changed", handleExamModeChanged);
    return () => window.removeEventListener("exam-mode-changed", handleExamModeChanged);
  }, []);

  const fetchReview = useCallback(async () => {
    if (!attemptId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/${getMockExamApiPrefix()}/mock-exam/attempt/${attemptId}/review`);
      const reviewData = res.data?.data ?? res.data;
      setData(reviewData);
      if (reviewData?.ai_feedback_status) {
        setAiFeedbackStatus(reviewData.ai_feedback_status);
      }
      if (reviewData?.ai_score != null) {
        setAiScore(reviewData.ai_score);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load review');
      toast.error('Failed to load review');
    } finally {
      setLoading(false);
    }
  }, [attemptId]);

  const fetchAiFeedback = useCallback(async () => {
    if (!attemptId) return;
    try {
      const res = await api.get(`/${getMockExamApiPrefix()}/mock-exam/attempt/${attemptId}/ai-feedback`);
      const payload = res.data?.data ?? res.data;
      const status = payload?.status ?? 'pending';
      setAiFeedbackStatus(status);
      if (status === 'completed') {
        setAiFeedbackMap(payload?.question_feedback ?? {});
        setAiOverall(payload?.overall ?? null);
        setAiScore(payload?.ai_score ?? null);
        setAiPercentage(payload?.ai_percentage ?? null);
        clearInterval(pollRef.current);
        // Re-fetch review data so the score header reflects the AI-adjusted score
        try {
          const reviewRes = await api.get(`/${getMockExamApiPrefix()}/mock-exam/attempt/${attemptId}/review`);
          const reviewData = reviewRes.data?.data ?? reviewRes.data;
          setData(reviewData);
        } catch {
          // Non-critical: keep existing review data
        }
      } else if (status === 'failed') {
        clearInterval(pollRef.current);
      }
    } catch {
      // Silently ignore polling errors
    }
  }, [attemptId]);

  useEffect(() => {
    fetchReview();
  }, [fetchReview]);

  // Start polling for AI feedback
  useEffect(() => {
    if (!attemptId) return;
    fetchAiFeedback();
    pollRef.current = setInterval(() => {
      pollCountRef.current += 1;
      if (pollCountRef.current >= MAX_POLLS) {
        clearInterval(pollRef.current);
        return;
      }
      if (aiFeedbackStatus === 'completed' || aiFeedbackStatus === 'failed') {
        clearInterval(pollRef.current);
        return;
      }
      fetchAiFeedback();
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [attemptId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to the question specified in the URL hash after data loads
  useEffect(() => {
    if (!loading && data && hash) {
      const id = hash.replace(/^#/, '');
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, [loading, data, hash]);

  useEffect(() => {
    if (aiFeedbackStatus === 'completed' || aiFeedbackStatus === 'failed') {
      clearInterval(pollRef.current);
    }
  }, [aiFeedbackStatus]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Card sx={{ p: 4, textAlign: 'center', maxWidth: 440 }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <HintIcon sx={{ fontSize: 32, color: '#374151' }} />
          </Box>
          <Typography variant="h6" gutterBottom color="text.secondary">
            Review could not be loaded
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {error || 'This attempt or review is not available. It may have been removed or you may not have access.'}
          </Typography>
          <Button variant="contained" onClick={() => navigate(getMockExamPortalHomePath())}>
            Back to Mock Exams
          </Button>
        </Card>
      </Box>
    );
  }

  const { exam_name, score, total_marks, percentage, questions = [] } = data;
  const totalDisplayQuestions = (questions || []).length;
  const displayPercentage = aiScore != null && total_marks > 0
    ? Math.round((aiScore / total_marks) * 1000) / 10
    : percentage;

  const computeLeafMarks = (q) => {
    const marks = Number(q.marks ?? 0);
    const aiq = aiFeedbackMap?.[String(q.id)];
    const ruleIsCorrect = aiq?.is_correct === true
      ? true
      : isAnswerCorrect(q.submitted_answer, q.correct_answer, q.question_text ?? q.question ?? '');
    const ruleIsPartial = !ruleIsCorrect && isAnswerPartial(q.submitted_answer, q.correct_answer, q.question_text ?? q.question ?? '');
    const aiIsPartial = aiq?.partial_score != null && aiq.partial_score > 0 && aiq.partial_score < 1;
    const effectivelyCorrect = aiq ? (aiq.is_correct === true || ruleIsCorrect) : ruleIsCorrect;
    const effectivelyPartial = !effectivelyCorrect && (ruleIsPartial || aiIsPartial);
    const hasAnswer = (q.submitted_answer != null && q.submitted_answer !== '')
      || !!q.submitted_drawing
      || (Array.isArray(q.your_answer_interactive) && q.your_answer_interactive.length > 0);

    let result;
    let earned = 0;
    if (effectivelyCorrect) {
      result = 'correct';
      earned = marks;
    } else if (effectivelyPartial) {
      result = 'partial';
      earned = aiq?.partial_score != null && aiq.partial_score > 0
        ? Math.round(aiq.partial_score * marks * 100) / 100
        : Math.round(marks * 0.5 * 100) / 100;
    } else if (!hasAnswer) {
      result = 'unanswered';
    } else {
      result = 'incorrect';
    }
    return { result, earned, max: marks };
  };

  const breakdownRows = [];
  questions.forEach((q, index) => {
    const mainNum = index + 1;
    if (q.type === 'grouped' && Array.isArray(q.sub_questions) && q.sub_questions.length > 0) {
      q.sub_questions.forEach((sub, si) => {
        const m = computeLeafMarks(sub);
        breakdownRows.push({
          key: sub.id ?? `${mainNum}-${si}`,
          qLabel: si === 0 ? `Q${mainNum}` : '',
          partLabel: String.fromCharCode(97 + si).toUpperCase(),
          questionText: sub.question_text,
          ...m,
          isMainRow: si === 0,
        });
      });
    } else {
      const m = computeLeafMarks(q);
      breakdownRows.push({
        key: q.id ?? index,
        qLabel: `Q${mainNum}`,
        partLabel: '',
        questionText: q.question_text,
        ...m,
        isMainRow: true,
      });
    }
  });

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Button size="small" sx={{ mb: 2 }} onClick={() => navigate(getMockExamPortalHomePath())}>
        Back to Mock Exams
      </Button>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {exam_name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Question-by-question review
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {aiScore != null ? (
              <>
                <Tooltip title="AI-adjusted score (semantic matching)">
                  <Chip
                    icon={<AiIcon sx={{ fontSize: 16 }} />}
                    label={`AI Score: ${Number(aiScore).toFixed(1)} / ${Number(total_marks).toFixed(1)}`}
                    color="secondary"
                    sx={{ fontWeight: 700 }}
                  />
                </Tooltip>
                <Chip label={`${Number(displayPercentage).toFixed(1)}%`} variant="outlined" color="secondary" />
                <Chip
                  label={`Auto Score: ${Number(aiScore).toFixed(1)} / ${Number(total_marks).toFixed(1)}`}
                  variant="outlined"
                  size="small"
                  sx={{ color: 'text.secondary' }}
                />
              </>
            ) : (
              <>
                <Chip label={`Score: ${Number(score).toFixed(1)} / ${Number(total_marks).toFixed(1)}`} color="primary" />
                <Chip label={`${Number(percentage).toFixed(1)}%`} variant="outlined" />
              </>
            )}
            {aiFeedbackStatus === 'pending' || aiFeedbackStatus === 'processing' ? (
              <Chip
                icon={<CircularProgress size={12} sx={{ color: '#7c3aed' }} />}
                label="AI feedback generating…"
                size="small"
                sx={{ bgcolor: '#f3f0ff', color: '#7c3aed', border: '1px solid #e0d7ff' }}
              />
            ) : aiFeedbackStatus === 'completed' ? (
              <Chip
                icon={<AiIcon sx={{ fontSize: 14 }} />}
                label="AI feedback ready"
                size="small"
                sx={{ bgcolor: '#f3f0ff', color: '#7c3aed', border: '1px solid #e0d7ff' }}
              />
            ) : null}
          </Box>

          {/* AI Overall Summary */}
          {aiOverall && (
            <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: '#f3f0ff', border: '1px solid #e0d7ff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AiIcon sx={{ fontSize: 18, color: '#7c3aed' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#7c3aed' }}>
                  AI Performance Summary
                </Typography>
                {aiOverall.performance_level && (
                  <Chip
                    label={aiOverall.performance_level.replace('_', ' ')}
                    size="small"
                    sx={{
                      bgcolor: {
                        excellent: '#dcfce7',
                        good: '#dbeafe',
                        average: '#fef9c3',
                        needs_improvement: '#fee2e2',
                        unknown: '#f3f4f6',
                      }[aiOverall.performance_level] ?? '#f3f4f6',
                      color: {
                        excellent: '#166534',
                        good: '#1e40af',
                        average: '#854d0e',
                        needs_improvement: '#991b1b',
                        unknown: '#374151',
                      }[aiOverall.performance_level] ?? '#374151',
                      fontWeight: 700,
                      textTransform: 'capitalize',
                    }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                {aiOverall.strengths?.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#166534', display: 'block', mb: 0.5 }}>
                      ✓ Strengths
                    </Typography>
                    {aiOverall.strengths.map((s, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.6 }}>
                        • {s}
                      </Typography>
                    ))}
                  </Box>
                )}
                {aiOverall.weaknesses?.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#991b1b', display: 'block', mb: 0.5 }}>
                      ✗ Areas to Improve
                    </Typography>
                    {aiOverall.weaknesses.map((w, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.6 }}>
                        • {w}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
              {aiOverall.recommendations?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', display: 'block', mb: 0.5 }}>
                    📚 Study Recommendations
                  </Typography>
                  {aiOverall.recommendations.map((r, i) => (
                    <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.6 }}>
                      {i + 1}. {r}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Tabs value={reviewTab} onChange={(_, v) => setReviewTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 3 }}>
        <Tab value="questions" label="Questions" />
        <Tab value="marks" label="Marks Breakdown" />
      </Tabs>

      {reviewTab === 'marks' && (
        <MarksBreakdownTable
          rows={breakdownRows}
          totalMarks={total_marks ?? 0}
          scoredMarks={aiScore != null ? aiScore : Number(score ?? 0)}
          title="Marks Breakdown"
          fileName={`${(exam_name || 'mock-exam').replace(/[^\w-]+/g, '_').slice(0, 60)}-marks`}
        />
      )}

      {reviewTab === 'questions' && (
      <>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Questions {totalDisplayQuestions > 0 ? `(Total: ${totalDisplayQuestions})` : ''}
      </Typography>
      {questions.length === 0 ? (
        <Alert severity="info">No questions in this attempt.</Alert>
      ) : (
        questions.map((q, index) => {
          const prevSectionTitle = index > 0 ? questions[index - 1]?.section_title : null;
          const isNewSection = !!q.section_title && q.section_title !== prevSectionTitle;

          // ── Grouped question (shared stem + sub-questions) ──────────────────
          if (q.type === 'grouped') {
            const subs = q.sub_questions || [];
            const questionNumber = index + 1;

            // Resolve a single explanation video for the whole grouped question.
            // Prefer a sub-question video if present, otherwise fall back to the parent.
            const firstSubWithVideo = subs.find(
              (sq) => sq.explanation_video_url || sq.video_generation_status
            );
            const groupVideoUrl =
              q.explanation_video_url || firstSubWithVideo?.explanation_video_url || null;
            const groupVideoStatus =
              q.video_generation_status || firstSubWithVideo?.video_generation_status || null;

            return (
              <Fragment key={q.id}>
                {/* Section title divider — shown when section_title changes vs previous question */}
                {isNewSection && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      bgcolor: '#f3e5f5',
                      borderRadius: 1,
                      borderLeft: '3px solid #9c27b0',
                      '& ol': { pl: 2, m: 0 },
                      '& li': { mb: 0.5, fontSize: '0.875rem', color: '#6a1b9a', lineHeight: 1.6 },
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#7b1fa2', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: q.section_instructions ? 0.5 : 0 }}>
                      {q.section_title}
                    </Typography>
                    {q.section_instructions && (
                      <FormatInstructions text={q.section_instructions} typographySx={{ color: '#6a1b9a', fontSize: '0.875rem' }} />
                    )}
                  </Box>
                )}
                <Card id={`question-${q.id}`} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  {q.section_title && (
                    <Chip
                      label={q.section_title}
                      size="small"
                      sx={{ mb: 1.5, bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 600 }}
                    />
                  )}
                  {/* Shared stem header */}
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      bgcolor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}
                    >
                      Shared Stem / Passage
                    </Typography>
                    <Box sx={{ fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.7 }}>
                      <HtmlWithLatex html={cleanStemText(q.question_text)} />
                    </Box>
                    {(() => {
                      const imgs =
                        Array.isArray(q.image_urls) && q.image_urls.filter(Boolean).length > 0
                          ? q.image_urls.filter(Boolean)
                          : q.image_url
                            ? [q.image_url]
                            : [];
                      return imgs.length > 0 ? (
                        <Box sx={{ mt: 1.5, display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: "center" }}>
                          {imgs.map((url, idx) => (
                            <img
                              key={idx}
                              src={url}
                              alt={imgs.length > 1 ? `Shared passage diagram ${idx + 1}` : "Shared passage diagram"}
                              style={{ maxWidth: "100%", maxHeight: 280, borderRadius: 6, border: "1px solid #e0e0e0", display: "block" }}
                              onError={(e) => { e.target.style.display = "none"; }}
                            />
                          ))}
                        </Box>
                      ) : null;
                    })()}
                  </Box>

                  {/* Sub-questions */}
                  {subs.map((sq, sqIdx) => {
                    const sqSubmitted = formatAnswer(sq.submitted_answer);
                    const sqCorrect = formatAnswer(sq.correct_answer);
                    const sqAiq = aiFeedbackMap?.[String(sq.id)];
                    const sqSubmittedArr = coerceFillAnswersArray(sq.submitted_answer, sq.correct_answer);
                    const sqCorrectArr = Array.isArray(sq.correct_answer) ? sq.correct_answer : null;
                    const sqIsTableQ = sq.type === 'table' && sq.answer_data?.table_layout?.grid?.length > 0;
                    const sqIsMultiFill = !sqIsTableQ && isPerBlankFillDisplay(sq, sqSubmittedArr, sqCorrectArr);
                    const sqRuleIsCorrect = (sqIsTableQ || sqIsMultiFill) && sqCorrectArr
                      ? sqCorrectArr.every((c, i) => singleValueMatches(String(sqSubmittedArr[i] ?? ''), String(c)))
                      : isAnswerCorrect(sq.submitted_answer, sq.correct_answer, sq.question_text ?? sq.question ?? q.question_text ?? q.question ?? '');
                    const sqRuleIsPartial = !sqRuleIsCorrect && isAnswerPartial(sq.submitted_answer, sq.correct_answer, sq.question_text ?? sq.question ?? q.question_text ?? q.question ?? '');
                    const sqIsCorrect = sqAiq?.is_correct != null ? sqAiq.is_correct : sqRuleIsCorrect;
                    const sqAiSaysWrongButRuleCorrect = sqAiq?.is_correct !== true && sqRuleIsCorrect;
                    const sqDisplayIsCorrect = sqAiSaysWrongButRuleCorrect ? true : sqIsCorrect;
                    const sqBlankLabels = sq.answer_data?.blank_labels ?? [];
                    const sqIsHubSpokeOuter = sq.answer_data?.diagram_type === 'hub-spoke-outer';

                    const sqLabel = String.fromCharCode(97 + sqIdx);
                    // Strip repeated passage context (data table, etc.) from sub-question text
                    const sqCleanText = extractSubCore(sq.question_text, sqLabel);
                    const sqWithClean = { ...sq, question_text: sqCleanText };
                    const sqPaperQuestion =
                      isFillQuestionType(sq) &&
                      Array.isArray(sq.correct_answer) &&
                      !(sq.answer_data?.correct_blanks?.length > 0 || (sq.blanks?.length ?? 0) > 0)
                        ? { ...sqWithClean, answer_data: { ...(sqWithClean.answer_data || {}), correct_blanks: sq.correct_answer.map((a) => [a]) } }
                        : sqWithClean;

                    return (
                      <Box
                        key={sq.id}
                        sx={{
                          mb: sqIdx < (q.sub_questions.length - 1) ? 2 : 0,
                          p: 1.5,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          {sq.type?.replace(/-/g, ' ')}
                        </Typography>
                        <QuestionPaperLayout
                          question={sqPaperQuestion}
                          questionNumber={questionNumber}
                          subLabel={sqLabel}
                          showAnswers={true}
                          marks={sq.marks}
                          studentAnswers={sqIsTableQ || sqIsMultiFill ? sqSubmittedArr : sq.submitted_answer}
                          hideImage={true}
                        />
                        <Divider sx={{ my: 1.5 }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {sqIsMultiFill ? (
                            /* ── Per-blank labeled display for multi-fill sub-question ── */
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                                Your answer → Correct answer
                              </Typography>
                              {sqCorrectArr.map((correctVal, bIdx) => {
                                const studentVal = sqSubmittedArr[bIdx];
                                // For hub-spoke-outer use A/B/C/D; otherwise use the expression label
                                const blLabel = sqIsHubSpokeOuter
                                  ? (['A', 'B', 'C', 'D'][bIdx] ?? String.fromCharCode(65 + bIdx))
                                  : sqBlankLabels[bIdx];
                                const blOk = singleValueMatches(String(studentVal ?? ''), String(correctVal));
                                return (
                                  <Box
                                    key={bIdx}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}
                                  >
                                    {blLabel && (
                                      <Box sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.secondary', flexShrink: 0 }}>
                                        <HtmlWithLatex html={`${blLabel}${sqIsHubSpokeOuter ? ':' : ')'}`} />
                                      </Box>
                                    )}
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: studentVal == null || studentVal === ''
                                          ? 'text.secondary'
                                          : blOk ? 'success.main' : 'error.main',
                                      }}
                                    >
                                      {studentVal ?? '—'}
                                    </Typography>
                                    <Typography variant="body2" color="text.disabled">→</Typography>
                                    <Box sx={{ fontWeight: 500, fontSize: '0.875rem', color: 'text.primary' }}>
                                      <HtmlWithLatex html={String(correctVal)} />
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          ) : (
                            /* ── Standard flat display ── */
                            <>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  Your answer
                                  {sqRuleIsPartial && (
                                    <Typography component="span" variant="caption" sx={{ ml: 0.75, color: '#b45309', fontWeight: 700 }}>
                                      — Partially correct
                                    </Typography>
                                  )}
                                </Typography>
                                <Box
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                    color: (sq.submitted_answer == null || sq.submitted_answer === '') && !sq.submitted_drawing
                                      ? 'text.secondary'
                                      : sqDisplayIsCorrect
                                        ? 'success.main'
                                        : sqRuleIsPartial
                                          ? '#b45309'
                                          : sq.submitted_drawing
                                            ? 'warning.main'
                                            : 'error.main',
                                  }}
                                >
                                  <HtmlWithLatex html={sqSubmitted} />
                                </Box>
                                {sq.submitted_drawing && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontStyle: 'italic' }}>
                                      Handwritten working:
                                    </Typography>
                                    <img
                                      src={sq.submitted_drawing}
                                      alt="Student handwritten working"
                                      style={{ maxWidth: '100%', border: '1px solid #e0e0e0', borderRadius: 4, display: 'block' }}
                                    />
                                  </Box>
                                )}
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {sqRuleIsPartial ? 'Full answer' : 'Correct answer'}
                                </Typography>
                                <Box sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                  <HtmlWithLatex html={sqCorrect} />
                                </Box>
                              </Box>
                            </>
                          )}
                          {(sq.correct_interactive || sq.answer_data?.interactive) && (
                            <InteractiveAnswerBlock row={sq} />
                          )}
                          <AiFeedbackPanel
                            questionId={sq.id}
                            aiFeedbackMap={aiFeedbackMap}
                            aiFeedbackStatus={aiFeedbackStatus}
                            questionMarks={sq.marks}
                            ruleIsCorrect={sqRuleIsCorrect}
                            ruleIsPartial={sqRuleIsPartial}
                          />
                          {sq.explanation && (
                            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f8faff', borderRadius: 2, border: '1px solid #e8ecff' }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', display: 'block', mb: 0.5 }}>
                                Explanation
                              </Typography>
                              <Box sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                                <HtmlWithLatex html={sq.explanation} />
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                  {subs.length === 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      No sub-questions found for this grouped question.
                    </Alert>
                  )}

                  {/* Single explanation video per grouped question */}
                  {groupVideoUrl && groupVideoStatus === 'completed' && (
                    <Box sx={{ mt: 2 }}>
                      <VideoExplanationPlayer
                        videoUrl={groupVideoUrl}
                        questionText={q.question_text || `Question ${questionNumber}`}
                      />
                    </Box>
                  )}
                  {groupVideoStatus && groupVideoStatus !== 'completed' && !groupVideoUrl && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Explanation video: {String(groupVideoStatus).replace(/_/g, ' ')}…
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
              </Fragment>
            );
          }

          // ── Standalone question ─────────────────────────────────────────────
          const submitted = formatAnswer(q.submitted_answer);
          const correct = formatAnswer(q.correct_answer);
          const aiq = aiFeedbackMap?.[String(q.id)];
          // For multi-fill: show per-blank answers with labels
          const submittedArr = coerceFillAnswersArray(q.submitted_answer, q.correct_answer);
          const correctArr = Array.isArray(q.correct_answer) ? q.correct_answer : null;
          const isTableQ = q.type === 'table' && q.answer_data?.table_layout?.grid?.length > 0;
          const isMultiFillQ = !isTableQ && isPerBlankFillDisplay(q, submittedArr, correctArr);
          // Rule-based correctness — includes time (7.46=7:46pm), ordinals (3=3rd lap), etc.
          const ruleIsCorrect = (isTableQ || isMultiFillQ) && correctArr
            ? correctArr.every((c, i) => singleValueMatches(String(submittedArr[i] ?? ''), String(c)))
            : isAnswerCorrect(q.submitted_answer, q.correct_answer, q.question_text ?? q.question ?? '');
          const ruleIsPartial = !ruleIsCorrect && isAnswerPartial(q.submitted_answer, q.correct_answer, q.question_text ?? q.question ?? '');
          // AI-overridden correctness — used only when AI and rule agree (or AI not available)
          const isCorrect = aiq?.is_correct != null ? aiq.is_correct : ruleIsCorrect;
          // For display color: trust rule-based when it says correct/partial but AI disagrees
          const aiSaysWrongButRuleCorrect = aiq?.is_correct !== true && ruleIsCorrect;
          const displayIsCorrect = aiSaysWrongButRuleCorrect ? true : isCorrect;
          const questionNumber = index + 1;
          const qBlankLabels = q.answer_data?.blank_labels ?? [];
          const isHubSpokeOuterQ = q.answer_data?.diagram_type === 'hub-spoke-outer';

          const paperQuestion =
            isFillQuestionType(q) &&
            Array.isArray(q.correct_answer) &&
            !(q.answer_data?.correct_blanks?.length > 0 || (q.blanks?.length ?? 0) > 0)
              ? { ...q, answer_data: { ...(q.answer_data || {}), correct_blanks: q.correct_answer.map((a) => [a]) } }
              : q;

          return (
            <Fragment key={q.id}>
              {/* Section title divider — shown when section_title changes vs previous question */}
              {isNewSection && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    bgcolor: '#f3e5f5',
                    borderRadius: 1,
                    borderLeft: '3px solid #9c27b0',
                    '& ol': { pl: 2, m: 0 },
                    '& li': { mb: 0.5, fontSize: '0.875rem', color: '#6a1b9a', lineHeight: 1.6 },
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#7b1fa2', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: q.section_instructions ? 0.5 : 0 }}>
                    {q.section_title}
                  </Typography>
                  {q.section_instructions && (
                    <FormatInstructions text={q.section_instructions} typographySx={{ color: '#6a1b9a', fontSize: '0.875rem' }} />
                  )}
                </Box>
              )}
              <Card id={`question-${q.id}`} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                {q.section_title && (
                  <Chip
                    label={q.section_title}
                    size="small"
                    sx={{ mb: 1.5, bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 600 }}
                  />
                )}
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {q.type?.replace(/-/g, ' ')}
                </Typography>
                <QuestionPaperLayout
                  question={paperQuestion}
                  questionNumber={questionNumber}
                  showAnswers={true}
                  marks={q.marks}
                  studentAnswers={isTableQ || isMultiFillQ ? submittedArr : q.submitted_answer}
                />
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {isMultiFillQ ? (
                    /* ── Per-blank labeled display for multi-fill ── */
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        Your answer → Correct answer
                      </Typography>
                      {correctArr.map((correctVal, bIdx) => {
                        const studentVal = submittedArr[bIdx];
                        // For hub-spoke-outer use A/B/C/D; otherwise use the expression label
                        const label = isHubSpokeOuterQ
                          ? (['A', 'B', 'C', 'D'][bIdx] ?? String.fromCharCode(65 + bIdx))
                          : qBlankLabels[bIdx];
                        const blankOk = singleValueMatches(String(studentVal ?? ''), String(correctVal));
                        return (
                          <Box
                            key={bIdx}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}
                          >
                            {label && (
                              <Box sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.secondary', flexShrink: 0 }}>
                                <HtmlWithLatex html={`${label}${isHubSpokeOuterQ ? ':' : ')'}`} />
                              </Box>
                            )}
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: studentVal == null || studentVal === ''
                                  ? 'text.secondary'
                                  : blankOk ? 'success.main' : 'error.main',
                              }}
                            >
                              {studentVal ?? '—'}
                            </Typography>
                            <Typography variant="body2" color="text.disabled">→</Typography>
                            <Box sx={{ fontWeight: 500, fontSize: '0.875rem', color: 'text.primary' }}>
                              <HtmlWithLatex html={String(correctVal)} />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    /* ── Standard flat display for MCQ / short-answer / etc. ── */
                    <>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Your answer
                          {ruleIsPartial && (
                            <Typography component="span" variant="caption" sx={{ ml: 0.75, color: '#b45309', fontWeight: 700 }}>
                              — Partially correct
                            </Typography>
                          )}
                        </Typography>
                        <Box
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            color: (q.submitted_answer == null || q.submitted_answer === '') && !q.submitted_drawing
                              ? 'text.secondary'
                              : displayIsCorrect
                                ? 'success.main'
                                : ruleIsPartial
                                  ? '#b45309'
                                  : q.submitted_drawing
                                    ? 'warning.main'
                                    : 'error.main',
                          }}
                        >
                          <HtmlWithLatex html={submitted} />
                        </Box>
                        {q.submitted_drawing && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontStyle: 'italic' }}>
                              Handwritten working:
                            </Typography>
                            <img
                              src={q.submitted_drawing}
                              alt="Student handwritten working"
                              style={{ maxWidth: '100%', border: '1px solid #e0e0e0', borderRadius: 4, display: 'block' }}
                            />
                          </Box>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {ruleIsPartial ? 'Full answer' : 'Correct answer'}
                        </Typography>
                        <Box sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                          <HtmlWithLatex html={correct} />
                        </Box>
                      </Box>
                    </>
                  )}

                  {(q.correct_interactive || q.answer_data?.interactive) && (
                    <InteractiveAnswerBlock row={q} />
                  )}

                  {/* AI Feedback Panel */}
                  <AiFeedbackPanel
                    questionId={q.id}
                    aiFeedbackMap={aiFeedbackMap}
                    aiFeedbackStatus={aiFeedbackStatus}
                    questionMarks={q.marks}
                    ruleIsCorrect={ruleIsCorrect}
                    ruleIsPartial={ruleIsPartial}
                  />

                  {q.explanation && (
                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f8faff', borderRadius: 2, border: '1px solid #e8ecff' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', display: 'block', mb: 0.5 }}>
                        Explanation
                      </Typography>
                      <Box sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                        <HtmlWithLatex html={q.explanation} />
                      </Box>
                    </Box>
                  )}

                  {q.explanation_video_url && q.video_generation_status === 'completed' && (
                    <Box sx={{ mt: 2 }}>
                      <VideoExplanationPlayer
                        videoUrl={q.explanation_video_url}
                        questionText={q.question_text || `Question ${questionNumber}`}
                      />
                    </Box>
                  )}
                  {q.video_generation_status && q.video_generation_status !== 'completed' && !q.explanation_video_url && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Explanation video: {q.video_generation_status.replace(/_/g, ' ')}…
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
            </Fragment>
          );
        })
      )}
      </>
      )}
    </Box>
  );
};

export default StudentMockExamReview;
